import React from 'react';

import { createContext, useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useIssueTypeFields, {
  type IssueTypeFieldsMethods,
  type IssueTypeFieldsState
} from '~/modules/app/hooks/useIssueTypeFields';
import { type TemplateFormFields } from '~/modules/app/types';
import { type IntegrationTemplate } from '~/modules/common/api-client';

type IssueTypeFieldsStateContext = IssueTypeFieldsState &
  IssueTypeFieldsMethods;

const defaultIssueTypeFieldsContext: IssueTypeFieldsStateContext = {
  data: null,
  loading: false,
  error: null,
  priorities: null,
  getPriorityById: () => null
};

export const IssueTypeFieldsContext =
  createContext<IssueTypeFieldsStateContext>(defaultIssueTypeFieldsContext);

export const useFieldsContext = () => useContext(IssueTypeFieldsContext);

interface IssueTypeFieldsProviderProps {
  children: React.ReactNode;
  enterpriseId: string;
  connectionId: string;
  token: string;
  initialTemplate?: IntegrationTemplate;
}
export const IssueTypeFieldsProvider = ({
  children,
  enterpriseId,
  connectionId,
  token,
  initialTemplate
}: IssueTypeFieldsProviderProps) => {
  const { control } = useFormContext<TemplateFormFields>();
  const projectId = useWatch({ control, name: 'project' });
  const issueTypeId = useWatch({ control, name: 'issueType' });

  const initialProjectId = initialTemplate?.project_id || '';
  const initialIssueTypeId = initialTemplate?.issue_type_id || '';

  const projectIdToUse = projectId || initialProjectId;
  const issueTypeIdToUse = issueTypeId || initialIssueTypeId;

  const skipRequest = !projectIdToUse || !issueTypeIdToUse;

  const issueTypeFieldsState = useIssueTypeFields(
    enterpriseId,
    projectIdToUse,
    issueTypeIdToUse,
    connectionId,
    token,
    skipRequest
  );

  return (
    <IssueTypeFieldsContext.Provider value={issueTypeFieldsState}>
      {children}
    </IssueTypeFieldsContext.Provider>
  );
};
